import React, { useEffect, useState } from "react";
import { getToken, login } from "../../apis/authentication";
import SelectComponent from "../../ReusableComponents/SelectComponent";
import "./MultiStepForm.css";
import { validateGstinNumber } from "../../ReusableFunctions/reusableFunctions";

const CompanyDetails = React.forwardRef(({ onNext }, ref) => {
  const [formData, setFormData] = useState({
    name: "",
    city: { label: "Select city", value: undefined },
    state: { label: "Select state", value: undefined },
    country: { label: "Select Country", value: undefined },
    gstin : null
  });

  const [errors, setErrors] = useState({
    name: "",
    country: "",
    state: "",
    city: "",
    gstin: validateGstinNumber(formData.gstin)
  });

  const [initialData, setInitialData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState({
    country: false,
    state: false,
    city: false,
  });

  const initData = async () => {
    try {
      const dspData = await getDspDetails();
      await getCountries();
      await getStatesOfCountry(undefined, dspData.country?.value);
      await getCitiesOfState(undefined, dspData.state?.value);
    } catch (err) {
      console.log("Somehting went wrong while load inital data ");
    }
  };

  const validateName = (name) => {
    if (!name.trim()) {
      return "Company name is required";
    }
    if (!/^[A-Za-z0-9\s\-&,.'’]+$/.test(name)) {
      return "Please enter valid company name";
    }
    if (name !== name.trim()) {
      return "No whitespace at start or end";
    }
    return "";
  };
  const validateForm = () => {
    const newErrors = {
    name: validateName(formData.name),
    country: !formData.country?.value ? "Country is required" : "",
    state: !formData.state?.value ? "State is required" : "",
    city: "", 
    gstin: "",
  };

    setErrors(newErrors);

    return !Object.values(newErrors).some((error) => error);
  };

  const getDspDetails = async () => {
    try {
      const res = await login();
      if (res?.dsp) {
        const dspData = {
          name: res.dsp.name || "",
          id: res.dsp?.id,
          city: { label: res.dsp?.city, value: res.dsp?.city } || undefined,
          state: { label: res.dsp?.state, value: res.dsp?.state } || undefined,
          country:
            { label: res.dsp?.country, value: res.dsp?.country } || undefined,
          gstin: res.dsp.gstin || undefined
        };
        setFormData(() => dspData);
        setInitialData(() => dspData);
        return dspData;
      }
    } catch (err) {
      console.error("Error while getting dsp details", err);
    }
  };

  React.useImperativeHandle(ref, () => ({
    submitForm: () => {
      const isValid = validateForm();
      if (!isValid) {
        return false;
      }

      let data = {
        ...formData,
        country: formData.country?.label,
        state: formData.state?.label,
        city: formData.city?.label??"",
      };

      const hasChanged = initialData
        ? Object.keys(formData).some(
            (key) => formData[key] !== initialData[key]
          )
        : true;

      onNext(hasChanged ? data : null);
      return true;
    },
  }));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "name") {
      setErrors({
        ...errors,
        name: validateName(value),
      });
    }

  };

  const getCountries = async () => {
    setLoading((prev) => ({ ...prev, country: true }));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/dsp/countries`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCountries(data?.countries || []);
    } catch (err) {
      console.error("Failed to fetch countries", err);
    } finally {
      setLoading((prev) => ({ ...prev, country: false }));
    }
  };

  const getStatesOfCountry = async (countryId, countryName) => {
    setLoading((prev) => ({ ...prev, state: true }));
    try {
          const url = `${process.env.REACT_APP_API_URL}/dsp/countries/states/${countryId}?countryName=${encodeURIComponent(countryName)}`;
      const response = await fetch(url,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setStates(data?.states || []);
    } catch (err) {
      console.error("Failed to fetch states", err);
    } finally {
      setLoading((prev) => ({ ...prev, state: false }));
    }
  };

  const getCitiesOfState = async (stateCode, stateName) => {
    setLoading((prev) => ({ ...prev, city: true }));
    try {
         const url = `${process.env.REACT_APP_API_URL}/dsp/countries/states/cities/${stateCode}?stateName=${encodeURIComponent(stateName)}`;
      const response = await fetch(url,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setCities(data?.cities || []);
    } catch (err) {
      console.error("Failed to fetch cities", err);
    } finally {
      setLoading((prev) => ({ ...prev, city: false }));
    }
  };

  useEffect(() => {
    initData();
  }, []);


  return (
    <div style={{ maxWidth: "500px" }}>
      <div className="form-header">
        <div className="form-title">Company Details</div>
      </div>

      {/* Name Field */}
      <div className="form-group" style={{}}>
        <div style={{ height: "65px" }}>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            placeholder="Enter company name"
            onChange={handleChange}
            required
            style={{
              width: "100%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.name ? "1px solid #D92D20" : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.name && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                marginTop: "4px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.name}
            </div>
          )}
        </div>
      </div>

      {/* Country Field */}
      <div className="form-group">
        <div style={{ height: "65px" }}>
          <SelectComponent
            isLoading={loading?.country}
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.country
                ? "1px solid #D92D20"
                : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
            isSearchable={true}
            placeholder={"Select country"}
            menuPlacement={"bottom"}
            list={countries?.map((c) => ({ value: c.name, label: c.name }))}
            value={formData?.country?.value ? formData.country : null}
            onChange={async (data) => {
              if (data.value && typeof data.value) {
                await getStatesOfCountry(undefined, data.value);
              }
              setFormData((prev) => ({
                ...prev,
                country: data,
                state: null,
                city: null,
              }));
              setErrors((prev) => ({ ...prev, country: "" }));
            }}
          />
          {errors.country && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                marginTop: "4px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.country}
            </div>
          )}
        </div>
      </div>

      {/* State Field */}
      <div className="form-group">
        <div style={{ height: "65px" }}>
          <SelectComponent
            isLoading={loading?.state}
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.state ? "1px solid #D92D20" : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
            isDisabled={loading.state}
            isSearchable={true}
            placeholder={"Select state"}
            menuPlacement={"bottom"}
            list={states?.map((c) => ({ value: c.name, label: c.name }))}
            value={formData?.state?.value ? formData.state : null}
            onChange={async (data) => {
              if (data?.value && typeof data?.value) {
                await getCitiesOfState(undefined, data?.value);
              }
              setFormData((prev) => ({ ...prev, state: data, city: null }));
              setErrors((prev) => ({ ...prev, state: "" }));
            }}
          />
          {errors.state && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                marginTop: "4px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.state}
            </div>
          )}
        </div>
      </div>

      {/* City Field */}
      <div className="form-group">
        <div style={{ height: "65px" }}>
          <SelectComponent
            isLoading={loading?.city}
            controlStyle={{
              height: "40px",
              width: "100%",
              borderRadius: "4px",
              border: errors.city ? "1px solid #D92D20" : "1px solid #D0D5DD",
              background: "white",
              fontSize: "14px",
              fontWeight: "500",
              color: "#1E1E1E",
              boxShadow: "none",
            }}
             isDisabled={loading.city}
            isSearchable={true}
            placeholder={"Select city"}
            menuPlacement={"bottom"}
            list={cities?.map((c) => ({ value: c.name, label: c.name }))}
            value={formData?.city?.value ? formData.city : null}
            onChange={(data) => {
              setFormData((prev) => ({ ...prev, city: data }));
              setErrors((prev) => ({ ...prev, city: "" }));
            }}
          />
          {errors.city && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                marginTop: "4px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.city}
            </div>
          )}
        </div>
      </div>
      {/* GSTIN Field */}
      <div className="form-group" style={{}}>
        <div style={{ height: "65px" }}>
          <input
            type="text"
            id="gstin"
            name="gstin"
            value={formData.gstin}
            placeholder="Enter GSTIN number"
            onChange={handleChange}
            required
            style={{
              width: "100%",
              height: "40px",
              padding: "8px 12px",
              borderRadius: "4px",
              border: errors.gstin ? "1px solid #D92D20" : "1px solid #D0D5DD",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          {errors.gstin && (
            <div
              style={{
                color: "#D92D20",
                fontSize: "12px",
                marginTop: "4px",
                height: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {errors.gstin}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default CompanyDetails;
